import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@repo/ui";
import { useCandidateAuthStore } from "@repo/auth";
import { AlertCircle, Lock } from "lucide-react";

export function ParcoursGuard() {
  const { id } = useParams<{ id: string }>();
  const { token, setToken, setFirstName, setEmail, setParcoursId } =
    useCandidateAuthStore();

  const [currentToken, setCurrentToken] = useState<string | undefined>(token);
  const [error, setError] = useState<string | undefined>();

  // Fetch metadata first (no auth) to know if parcours is anonymous
  const metadata = useQuery(
    api.parcoursEvaluations.obtenirMetadonneesParcours,
    id ? { parcoursId: id as Id<"parcoursEvaluations"> } : "skip",
  );

  const estAnonyme = metadata?.anonyme === true;

  // Query to verify access
  const result = useQuery(
    api.parcoursEvaluations.verifierAccesParcours,
    id && currentToken
      ? {
          parcoursId: id as Id<"parcoursEvaluations">,
          identifiant: currentToken,
        }
      : "skip",
  );

  // Update stored values when validation succeeds
  useEffect(() => {
    if (result && result.statut === "ok" && id && currentToken) {
      setToken(currentToken);
      setParcoursId(id);
      if (result.prenomCandidat) setFirstName(result.prenomCandidat);
      if (result.emailCandidat) setEmail(result.emailCandidat);
      setError(undefined);
    }
  }, [
    result,
    id,
    currentToken,
    setToken,
    setParcoursId,
    setFirstName,
    setEmail,
  ]);

  // Handle modal submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const value = formData.get("identifiant") as string;
    if (value?.trim()) {
      setError(undefined);
      setCurrentToken(value.trim());
    }
  };

  // No ID in URL
  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Lien invalide</h1>
          <p className="mt-2 text-muted-foreground">
            Le lien d'accès est invalide ou incomplet.
          </p>
        </div>
      </div>
    );
  }

  // Loading metadata
  if (metadata === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const label = estAnonyme ? "Token d'accès" : "Nom de famille";
  const placeholder = estAnonyme
    ? "Entrez votre token (ex: abc-1234)"
    : "Entrez votre nom de famille";
  const autoComplete = estAnonyme ? "off" : "family-name";

  // No token yet - show modal
  if (!currentToken) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Vérification d'identité</CardTitle>
            <CardDescription>
              {estAnonyme
                ? "Pour accéder à votre parcours d'évaluations, veuillez saisir le token qui vous a été communiqué."
                : "Pour accéder à votre parcours d'évaluations, veuillez saisir votre nom de famille tel qu'il a été enregistré."}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifiant">{label}</Label>
                <Input
                  id="identifiant"
                  name="identifiant"
                  type="text"
                  placeholder={placeholder}
                  autoFocus
                  autoComplete={autoComplete}
                />
              </div>
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="w-full">
                Accéder au parcours
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // Loading state
  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error from query
  if (result.statut === "erreur") {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Erreur d'accès</CardTitle>
            <CardDescription>{result.message}</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifiant">{label}</Label>
                <Input
                  id="identifiant"
                  name="identifiant"
                  type="text"
                  placeholder={placeholder}
                  autoFocus
                  autoComplete={autoComplete}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>{error}</p>
                </div>
              )}
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="w-full">
                Réessayer
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  return <Outlet />;
}
